import React from 'react';
import { Tooltip } from 'antd';
import { FaCheck, FaInfoCircle, FaStar, FaSuitcase } from 'react-icons/fa';
import { MdEventSeat, MdRestaurant } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import './amenitiesTooltip.less';

const ICON_BY_TYPE: Record<string, React.ComponentType> = {
  BAGGAGE: FaSuitcase,
  'PRIORITY BAGGAGE': FaSuitcase,
  PRE_RESERVED_SEAT: MdEventSeat,
  MEAL: MdRestaurant,
  BRANDED_FARES: FaStar,
};

/** The supplier writes "FIRST CHECKED BAG" - read it as "First checked bag" */
const sentenceCase = (text?: string): string => {
  const value = String(text ?? '').trim();
  return value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : '';
};

interface Props {
  flightFrom: any;
  /** "Full info" before the icon */
  isShowFullText?: boolean;
  /** the fare name after the icon - off where the fare name is already shown next to it */
  showFareName?: boolean;
}

/** What the ticket includes (the supplier's non-chargeable services), one line each with an icon */
const AmenitiesTooltip = ({ flightFrom, isShowFullText = true, showFareName = true }: Props) => {
  const { viewFlight: t } = useSelector(
    (rootState: RootState) => rootState.config.translations
  );

  if (!flightFrom?.fare?.name) return null;
  const included = (flightFrom?.fare?.additional_services ?? []).filter(
    (item: any) => !item.isChargeable
  );
  if (!included.length) {
    return null;
  }

  return (
    <Tooltip
      overlayClassName="amenities-tooltip"
      color="#fff"
      title={
        <div className="amenities-tooltip__body">
          <div className="amenities-tooltip__title">{flightFrom.fare.name}</div>
          <ul className="amenities-tooltip__list">
            {included.map((item: any, index: number) => {
              const Icon = ICON_BY_TYPE[item?.amenityType] ?? FaCheck;
              const description = t[item.description] ?? sentenceCase(item.description);
              const type = item?.amenityType ? t[item.amenityType] ?? item.amenityType : '';
              return (
                <li key={`${item?.code ?? item?.description}-${index}`}>
                  <span className="amenities-tooltip__icon">
                    <Icon />
                  </span>
                  <span className="amenities-tooltip__text">
                    <span className="amenities-tooltip__desc" dir="auto">
                      {description}
                    </span>
                    {!!type && <span className="amenities-tooltip__type">{type}</span>}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      }
    >
      <span className="amenities-tooltip__trigger">
        {isShowFullText && t.fullInfo}
        <FaInfoCircle />
        {showFareName && flightFrom.fare.name}
      </span>
    </Tooltip>
  );
};

export default AmenitiesTooltip;
